import React, { useState, useEffect } from 'react'
import { FormPartInterface } from '../../../interfaces'
import { FieldGroup, FormCard, SubmitButton, Field } from '../../reusable'
import { Checkbox } from '../../reusable/checkbox'
import { useClaimData, useUpdateClaimData } from '../../../claimProvider'
import { sectionValidation } from '../../../helpers'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Loading } from 'notiflix'
import styles from './styles.module.scss'
import responsiveStyles from './responsiveStyles.module.scss'

export const ConfirmDetails = (props: FormPartInterface) => {
    const { isActive, assignGroup, saveValueFunct } = props
    const [isContactConsentConfirmed, setIsContactConsentConfirmed] = useState(false)
    const [isMarketingConsentConfirmed, setIsMarketingConsentConfirmed] = useState(false)
    const [stepData, setStepData] = useState<any>({})
    const [currentStep, setCurrentStep] = useState(0)
    const [postCurrentStep, setPostCurrentStep] = useState(0)
    const claimData = useClaimData()
    const updateClaim = useUpdateClaimData()

    useEffect(() => {
        // Check existing consent values
        if (claimData.contact_consent === 'yes') {
            setIsContactConsentConfirmed(true)
        }
        if (claimData.marketing_consent === 'yes') {
            setIsMarketingConsentConfirmed(true)
        }
    }, [claimData])

    const handleProceed = () => {
        try {
            if (!isContactConsentConfirmed) {
                Report.failure('Error', 'You must consent to be contacted to proceed with your claim.', 'OK')
                return
            }

            if (!isMarketingConsentConfirmed) {
                Report.failure('Error', 'You must consent to marketing communications to proceed.', 'OK')
                return
            }

            Loading.standard('Processing your submission...')

            let alternateAddressesData = null
            const previousAddresses = formatPreviousAddresses()

            if (previousAddresses && previousAddresses.length > 0) {
                alternateAddressesData = previousAddresses.map((address: any) => ({
                    client_id: claimData.client_id,
                    house_number: '',
                    house_name: '',
                    flat: '',
                    street: '',
                    town_city: address.town || '',
                    county: address.county || '',
                    postcode: address.postcode || '',
                    address_line_one: address.line_one || '',
                    address_line_two: address.line_two || '',
                    address_line_three: address.line_three || '',
                    raw_address: address.text || '',
                }))
            } else {
                console.log('No previous addresses provided by user')
            }

            const updatedData = {
                ...claimData,
                ...stepData,
                contact_consent: 'yes',
                marketing_consent: 'yes',
                type: 'webform_SENTINEL_ALTERNATE',
                client_id: claimData.client_id,
                alternate_addresses: alternateAddressesData,
            }

            updateClaim(updatedData)

            setTimeout(() => {
                assignGroup('signature')
                setTimeout(() => {
                    Loading.remove()
                }, 1000)
            }, 800)
        } catch (error: any) {
            Loading.remove()
            Report.failure('Error', error.message, 'OK')
        }
    }

    const formatAddress = () => {
        const addressParts = [
            claimData.address_line_one,
            claimData.address_line_two,
            claimData.address_line_three,
            claimData.city,
            claimData.postcode,
        ].filter(Boolean)

        return (
            <>
                {addressParts.map((part, index) => (
                    <React.Fragment key={index}>
                        {part}
                        {index < addressParts.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </>
        )
    }

    const formatPreviousAddresses = () => {
        try {
            const previousAddressesData = claimData.previous_addresses
            if (!previousAddressesData) return null

            let addresses = []
            if (typeof previousAddressesData === 'string') {
                addresses = JSON.parse(previousAddressesData)
            } else if (Array.isArray(previousAddressesData)) {
                addresses = previousAddressesData
            }

            if (!addresses || addresses.length === 0) return null

            return addresses
        } catch (error) {
            console.error('Error parsing previous addresses:', error)
            return null
        }
    }

    const getCurrentStep = () => {
        return isActive === 'confirm' ? (
            <FieldGroup title="Confirm Your Details">
                <FormCard step={currentStep} assignPostStep={setPostCurrentStep} onBack={() => assignGroup('contact')}>
                    <h3>Please Review Your Details</h3>
                    <p>Please check that all the information below is correct before proceeding.</p>
                    <div className={`${styles.card} ${responsiveStyles.card}`}>
                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Personal Information
                            </h4>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Title:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.title || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    First Name:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.firstname || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    Last Name:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.lastname || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    Date of Birth:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.dob || 'Not provided'}
                                </span>
                            </div>
                        </div>

                        <hr className={`${styles.divider} ${responsiveStyles.divider}`} />

                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Current Address
                            </h4>
                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Address:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {formatAddress()}
                                </span>
                            </div>
                        </div>

                        {formatPreviousAddresses() && (
                            <>
                                <hr className={`${styles.divider} ${responsiveStyles.divider}`} />
                                <div className={`${styles.section} ${responsiveStyles.section}`}>
                                    <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                        Previous Addresses (Last 3 Years)
                                    </h4>
                                    {formatPreviousAddresses()?.map((address: any, index: number) => (
                                        <div
                                            key={index}
                                            className={`${styles.field} ${responsiveStyles.field}`}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                                Address {index + 1}:
                                            </span>
                                            <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                                {address.line_one}
                                                {address.line_two && (
                                                    <>
                                                        <br />
                                                        {address.line_two}
                                                    </>
                                                )}
                                                {address.line_three && (
                                                    <>
                                                        <br />
                                                        {address.line_three}
                                                    </>
                                                )}
                                                <br />
                                                {address.town}
                                                {address.county && `, ${address.county}`}
                                                <br />
                                                {address.postcode}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        <hr className={`${styles.divider} ${responsiveStyles.divider}`} />

                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Contact Information
                            </h4>
                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Email:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.email || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Phone:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.telephone || 'Not provided'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="contact_consent"
                            label="I confirm that I am happy to be contacted by Sentinel Legal Limited in relation to my enquiry. This may include phone calls, emails, SMS text messages, and written correspondence about progressing my potential car finance claim."
                            initialValue={claimData.contact_consent}
                            saveValue={(data) => {
                                setIsContactConsentConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="marketing_consent"
                            label="I consent to receiving updates, offers, and information from Sentinel Legal about related legal services and promotions via email, phone, or SMS. I understand I can opt out at any time by contacting Sentinel Legal or using the unsubscribe link in any message."
                            initialValue={claimData.marketing_consent}
                            saveValue={(data) => {
                                setIsMarketingConsentConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    <div className={styles.dataPolicy}>
                        <p>
                            By submitting this form, you agree to our{' '}
                            <a
                                href="https://www.sentinellegal.co.uk/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.policyLink}
                            >
                                Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                                href="https://www.sentinellegal.co.uk/terms-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.policyLink}
                            >
                                Terms & Conditions
                            </a>
                            . These explain how we use, store, and protect your data.
                        </p>
                    </div>

                    <SubmitButton
                        text="Proceed to Signature"
                        btnType="primary"
                        onClick={handleProceed}
                        disabled={!isContactConsentConfirmed || !isMarketingConsentConfirmed}
                    />
                </FormCard>
            </FieldGroup>
        ) : (
            <></>
        )
    }

    return getCurrentStep()
}
